// FILE: src/store.js
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // localStorage

import adminUsersReducer from "./features/adminUsersSlice";
import resourceReducer from "./features/resourceSlice";
import auditLogsReducer from "./features/auditLogsSlice";
import complianceReducer from "./features/complianceSlice";
import ticketReducer from "./features/ticketSlice";
import feedbackReducer from "./features/feedbackSlice";
import authReducer from "./features/authSlice";
import dashboardReducer from "./features/dashboardSlice";
import progressReducer from "./features/progressSlice";
import supportReducer from "./features/supportSlice";
import profileReducer from "./features/profileSlice";
import myActivityReducer from "./features/myActivitySlice";
import course2faReducer from "./features/course2faSlice";
import supportPromptReducer from "./features/supportPromptSlice";
import timeReducer from "./features/timeSlice";
import onboardingReducer from "./features/onboardingSlice";

const appReducer = combineReducers({
  auth: authReducer,
  adminUsers: adminUsersReducer,
  auditLogs: auditLogsReducer,
  resources: resourceReducer,
  compliance: complianceReducer,
  tickets: ticketReducer,
  feedback: feedbackReducer,
  dashboard: dashboardReducer,
  progress: progressReducer,
  support: supportReducer,
  profile: profileReducer,
  myActivity: myActivityReducer,
  course2fa: course2faReducer,
  supportPrompt: supportPromptReducer,
  time: timeReducer,
  onboarding: onboardingReducer
});

// Signing out (or being signed out) wipes every slice, not just auth (B-20).
export const RESET_ACTIONS = [
  "auth/logoutUser/fulfilled",
  "auth/logoutUser/rejected",
  "auth/sessionEnded"
];

const rootReducer = (state, action) =>
  RESET_ACTIONS.includes(action.type) ? appReducer(undefined, action) : appReducer(state, action);

// AD-9: only who is signed in is kept across reloads — user id, role and
// display name, and for staff the role's name and permissions (RBAC: no
// personal data; refreshed by /api/auth/me on every page). Progress,
// tickets, users, logs etc. are always re-fetched.
const authOnly = createTransform(
  (auth) => ({
    user: auth.user
      ? {
          _id: auth.user._id,
          role: auth.user.role,
          firstName: auth.user.firstName,
          lastName: auth.user.lastName,
          ...(Array.isArray(auth.user.permissions) ? { permissions: auth.user.permissions } : {}),
          ...(auth.user.staffRole ? { staffRole: { id: auth.user.staffRole.id, name: auth.user.staffRole.name } } : {})
        }
      : null,
    role: auth.role
  }),
  (saved) => ({ ...saved }),
  { whitelist: ["auth"] }
);

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth"],
  transforms: [authOnly]
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

let persistorRef = null;
const purgeOnReset = () => (next) => (action) => {
  const result = next(action);
  if (RESET_ACTIONS.includes(action.type) && persistorRef) persistorRef.purge();
  return result;
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat(purgeOnReset)
});

export const persistor = persistStore(store);
persistorRef = persistor;
